import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import SearchSelect from "@/components/common/SearchSelect";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { fmtINR, today } from "@/lib/format";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import QCRecordModal from "@/components/QCRecordModal";

/** Generic Credit/Debit Notes register + create form.
 *  `kind = "credit" | "debit"` — chooses endpoints, entity type, and terminology. */
export default function NotesRegister({ kind = "credit" }) {
  const isCredit = kind === "credit";
  const noun = isCredit ? "Credit Note" : "Debit Note";
  const partyLabel = isCredit ? "Customer" : "Vendor";
  const listEp = isCredit ? "/credit-notes" : "/debit-notes";
  const linkedEp = isCredit ? "/sales" : "/purchases";
  const linkedField = isCredit ? "against_invoice_id" : "against_bill_id";
  const linkedNumField = isCredit ? "against_invoice_number" : "against_bill_number";
  const partyField = isCredit ? "customer_id" : "vendor_id";
  const linkedIdKey = isCredit ? "invoice_number" : "bill_number";

  const [rows, setRows] = useState([]);
  const [parties, setParties] = useState([]);
  const [linkedDocs, setLinkedDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [qcTarget, setQcTarget] = useState(null);
  const [detailNote, setDetailNote] = useState(null);
  const empty = {
    [partyField]: "", [linkedField]: "", [linkedNumField]: "",
    reason: isCredit ? "sales_return" : "purchase_return",
    date: today(),
    subtotal: 0, tax_total: 0, grand_total: 0, notes: "",
  };
  const [form, setForm] = useState(empty);

  const load = async () => setRows((await api.get(listEp)).data);
  useEffect(() => {
    load();
    api.get("/parties", { params: { party_type: isCredit ? "customer" : "vendor" } })
      .then((r) => setParties(r.data));
    // eslint-disable-next-line
  }, [kind]);

  useEffect(() => {
    if (!form[partyField]) return setLinkedDocs([]);
    api.get(linkedEp, { params: { [partyField]: form[partyField] } })
      .then((r) => setLinkedDocs(r.data || [])).catch(() => setLinkedDocs([]));
    // eslint-disable-next-line
  }, [form[partyField]]);

  const create = async () => {
    if (!form[partyField]) return toast.error(`${partyLabel} required`);
    if (!form.grand_total) return toast.error("Grand total required");
    try {
      await api.post(listEp, form);
      toast.success(`${noun} posted`); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const partyOptions = useMemo(() => parties.map((p) => ({ value: p.id, label: p.name, sublabel: p.gstin })), [parties]);
  const linkedOptions = useMemo(() => [{ value: "", label: "— No underlying document —" }, ...linkedDocs.map((d) => ({ value: d.id, label: d[linkedIdKey], sublabel: fmtINR(d.grand_total) }))], [linkedDocs, linkedIdKey]);
  const total = rows.reduce((s, r) => s + (r.grand_total || 0), 0);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid={`${kind}-notes-page`}>
      <PageHeader eyebrow="Registers" title={`${noun}s`}
        description={isCredit
          ? "Reduce receivables — sales returns, discounts, corrections. Linked to the original invoice for AR-reconciliation."
          : "Reduce payables — purchase returns, disputed billings, price corrections. Linked to the original vendor bill."}
        actions={<Button onClick={() => setOpen(true)} className="rounded-sm bg-accent text-white" data-testid={`new-${kind}-note`}>+ {noun}</Button>} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">Total notes</div><div className="metric-value text-2xl mt-2">{rows.length}</div></div>
        <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">Value posted</div><div className="metric-value text-2xl mt-2">{fmtINR(total)}</div></div>
        <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">This month</div><div className="metric-value text-2xl mt-2">{rows.filter((r) => (r.date || "").startsWith(today().slice(0, 7))).length}</div></div>
      </div>

      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Note #</th><th>Date</th><th>{partyLabel}</th><th>Against</th><th>Reason</th><th className="text-right">Amount</th>{isCredit && <th></th>}</tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={isCredit ? "7" : "6"} className="text-center py-8 text-muted-foreground">No {noun.toLowerCase()}s yet</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`note-row-${r.note_number}`}
                    className="cursor-pointer hover:bg-muted/40 transition-colors"
                    onClick={() => setDetailNote(r)}>
                <td className="font-mono text-xs">{r.note_number}</td>
                <td className="text-xs">{r.date}</td>
                <td>{parties.find((p) => p.id === r[partyField])?.name || "—"}</td>
                <td className="text-xs font-mono">{r[linkedNumField] || "—"}</td>
                <td className="text-xs text-muted-foreground">{r.reason.replaceAll("_", " ")}</td>
                <td className={`text-right tabular-nums font-medium ${isCredit ? "text-red-600" : "text-emerald-600"}`}>{fmtINR(r.grand_total)}</td>
                {isCredit && (
                  <td className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1"
                              onClick={() => setQcTarget({ id: r.id, ref: r.note_number })}
                              data-testid={`qc-srn-${r.note_number}`}>
                      <ShieldCheck className="w-3 h-3" /> SRN QC
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader><SheetTitle className="font-heading">New {noun}</SheetTitle></SheetHeader>
          <div className="space-y-3">
            <div><Label>{partyLabel} *</Label>
              <SearchSelect options={partyOptions} value={form[partyField]}
                              onChange={(v) => setForm({ ...form, [partyField]: v })}
                              placeholder={`Select ${partyLabel.toLowerCase()}`} testId={`${kind}-note-party`} />
            </div>
            <div><Label>Against {isCredit ? "invoice" : "bill"}</Label>
              <SearchSelect options={linkedOptions} value={form[linkedField]}
                              onChange={(v) => {
                                const doc = linkedDocs.find((d) => d.id === v);
                                setForm({ ...form, [linkedField]: v, [linkedNumField]: doc?.[linkedIdKey] || "" });
                              }}
                              placeholder="Choose linked document" testId={`${kind}-note-linked`} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date *</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm mt-1" /></div>
              <div><Label>Reason</Label>
                <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="w-full h-9 rounded-sm border border-input bg-background mt-1 px-2 text-sm" data-testid={`${kind}-note-reason`}>
                  {(isCredit ? ["sales_return", "discount", "correction", "other"] : ["purchase_return", "discount", "correction", "other"]).map((r) => <option key={r} value={r}>{r.replaceAll("_", " ")}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Subtotal</Label><Input type="number" value={form.subtotal} onChange={(e) => setForm({ ...form, subtotal: Number(e.target.value) })} className="rounded-sm mt-1" /></div>
              <div><Label>Tax</Label><Input type="number" value={form.tax_total} onChange={(e) => setForm({ ...form, tax_total: Number(e.target.value) })} className="rounded-sm mt-1" /></div>
            </div>
            <div><Label>Grand total *</Label><Input type="number" value={form.grand_total} onChange={(e) => setForm({ ...form, grand_total: Number(e.target.value) })} className="rounded-sm mt-1" data-testid={`${kind}-note-total`} /></div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="rounded-sm mt-1" /></div>
          </div>
          <Button onClick={create} className="w-full rounded-sm bg-accent text-white" data-testid={`${kind}-note-save`}>Post {noun}</Button>
        </SheetContent>
      </Sheet>

      {isCredit && qcTarget && (
        <QCRecordModal
          open={!!qcTarget}
          onOpenChange={(o) => !o && setQcTarget(null)}
          kind="srn"
          sourceId={qcTarget.id}
          sourceRef={qcTarget.ref}
          onCreated={() => setQcTarget(null)}
        />
      )}

      {/* Iter-161 · Clickable drill-down for credit/debit note rows */}
      <Sheet open={!!detailNote} onOpenChange={(o) => !o && setDetailNote(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg rounded-none space-y-4" data-testid={`${kind}-note-detail-sheet`}>
          <SheetHeader className="pb-3 border-b">
            <SheetTitle className="font-heading">{noun} details</SheetTitle>
          </SheetHeader>
          {detailNote && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-[10px] uppercase font-mono text-muted-foreground">Note #</div><div className="font-mono">{detailNote.note_number}</div></div>
                <div><div className="text-[10px] uppercase font-mono text-muted-foreground">Date</div><div>{detailNote.date}</div></div>
                <div><div className="text-[10px] uppercase font-mono text-muted-foreground">{partyLabel}</div><div>{parties.find((p) => p.id === detailNote[partyField])?.name || "—"}</div></div>
                <div><div className="text-[10px] uppercase font-mono text-muted-foreground">Against</div><div className="font-mono text-xs">{detailNote[linkedNumField] || "—"}</div></div>
                <div><div className="text-[10px] uppercase font-mono text-muted-foreground">Reason</div><div>{(detailNote.reason || "").replaceAll("_", " ")}</div></div>
                <div><div className="text-[10px] uppercase font-mono text-muted-foreground">Status</div><div>{detailNote.status || "posted"}</div></div>
                <div><div className="text-[10px] uppercase font-mono text-muted-foreground">Subtotal</div><div className="tabular-nums">{fmtINR(detailNote.subtotal || 0)}</div></div>
                <div><div className="text-[10px] uppercase font-mono text-muted-foreground">Tax</div><div className="tabular-nums">{fmtINR(detailNote.tax_total || 0)}</div></div>
                <div><div className="text-[10px] uppercase font-mono text-muted-foreground">Grand total</div><div className="tabular-nums font-semibold">{fmtINR(detailNote.grand_total || 0)}</div></div>
                {detailNote.gl_voucher_number && (
                  <div><div className="text-[10px] uppercase font-mono text-muted-foreground">GL Voucher</div><div className="font-mono text-xs">{detailNote.gl_voucher_number}</div></div>
                )}
              </div>
              {detailNote.notes && (
                <div>
                  <div className="text-[10px] uppercase font-mono text-muted-foreground mb-1">Notes</div>
                  <div className="text-xs whitespace-pre-wrap">{detailNote.notes}</div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
